import { useEffect, useRef, useState, type TouchEvent } from 'react';
import { Link } from 'react-router-dom';
import type { ContentItem } from '@/lib/content';

interface HeroCarouselProps {
  slides: ContentItem[];
  news: ContentItem[];
}

export default function HeroCarousel({ slides, news }: HeroCarouselProps) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef(0);

  useEffect(() => {
    if (paused || slides.length <= 1) return;
    const timer = window.setInterval(() => {
      setActive((current) => (current + 1) % slides.length);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [paused, slides.length]);

  if (!slides.length) return null;
  const slide = slides[active];

  const goPrev = () => setActive((current) => (current - 1 + slides.length) % slides.length);
  const goNext = () => setActive((current) => (current + 1) % slides.length);

  const handleTouchStart = (event: TouchEvent<HTMLElement>) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const handleTouchEnd = (event: TouchEvent<HTMLElement>) => {
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 48) {
      if (delta < 0) goNext();
      else goPrev();
    }
  };

  return (
    <section
      className="relative w-full overflow-hidden bg-background-50"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="absolute inset-0">
        {slides.map((item, index) => (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-[1200ms] ease-smooth ${
              index === active ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={item.image}
              alt={`${item.title} 精彩片段画面`}
              title={`${item.title} - 我的朋友他的妻子 精彩片段`}
              className={`h-full w-full object-cover object-top ${index === active ? 'animate-slow-zoom' : ''}`}
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/92 to-background-50/45"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-background-50/70"></div>
        <div className="absolute inset-0 film-grain opacity-40"></div>
      </div>

      <div className="relative w-full px-4 md:px-8 lg:px-12 pb-14 pt-28 md:pb-20 md:pt-36">
        <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-[1.5fr_1fr] lg:gap-12">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <span className="rounded-full border border-primary-500/40 bg-primary-500/10 px-3 py-1 text-[11px] tracking-wide text-primary-300">
                2025 · 都市情感剧集
              </span>
              <span className="text-[11px] tracking-[0.2em] text-foreground-500 font-label">12 EPISODES</span>
            </div>

            <h1 className="mt-5 font-heading text-3xl font-black leading-tight text-foreground-950 md:text-5xl lg:text-6xl">
              我的朋友他的妻子
            </h1>

            <div key={slide.id} className="animate-fade-up">
              <p className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-1 font-heading text-lg text-primary-300 md:text-2xl">
                <span>{slide.title}</span>
                <span className="text-xs text-foreground-500 md:text-sm">{slide.dateLabel}</span>
              </p>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-700 md:text-base">
                {slide.excerpt}
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {slide.tags.slice(0, 4).map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full border border-background-300/60 px-2.5 py-0.5 text-[11px] text-foreground-400"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Link
                to="/story"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-400"
              >
                <i className="ri-book-open-line text-base"></i>
                从第一幕开始读
              </Link>
              <Link
                to="/cast"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300/70 px-6 py-3 text-sm text-foreground-300 transition-colors hover:border-primary-500/50 hover:text-primary-300"
              >
                <i className="ri-user-star-line text-base"></i>
                查看演员阵容
              </Link>
            </div>

            <div className="mt-10 flex items-center gap-4">
              <button
                type="button"
                onClick={goPrev}
                aria-label="上一张焦点内容"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-background-300/70 text-foreground-300 transition-colors hover:border-primary-500/60 hover:text-primary-300 cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
              </button>
              <button
                type="button"
                onClick={goNext}
                aria-label="下一张焦点内容"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-background-300/70 text-foreground-300 transition-colors hover:border-primary-500/60 hover:text-primary-300 cursor-pointer"
              >
                <i className="ri-arrow-right-line"></i>
              </button>
              <div className="flex items-center gap-2">
                {slides.map((item, index) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setActive(index)}
                    aria-label={`切换到第 ${index + 1} 张焦点内容`}
                    aria-current={index === active ? 'true' : undefined}
                    className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                      index === active ? 'w-8 bg-primary-500' : 'w-3 bg-foreground-700/40 hover:bg-foreground-600/60'
                    }`}
                  ></button>
                ))}
              </div>
              <span className="ml-1 font-label text-xs tracking-[0.2em] text-foreground-500">
                0{active + 1} / 0{slides.length}
              </span>
            </div>
          </div>

          <aside className="w-full rounded-lg border border-background-200/80 bg-background-100/85 p-5 backdrop-blur-md md:p-6">
            <div className="flex items-center justify-between gap-3">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-newspaper-line text-primary-400"></i>
                最新资讯
              </h2>
              <Link
                to="/news"
                className="whitespace-nowrap text-xs text-foreground-500 transition-colors hover:text-primary-400"
              >
                全部 &gt;
              </Link>
            </div>
            <ul className="mt-4 divide-y divide-background-200/70">
              {news.map((item) => (
                <li key={item.id}>
                  <Link to={`/detail/${item.id}`} className="group flex gap-3 py-3">
                    <span className="mt-0.5 font-label text-xs text-primary-500/80">{item.dateLabel.slice(5)}</span>
                    <span className="min-w-0">
                      <span className="line-clamp-2 text-sm leading-snug text-foreground-300 transition-colors group-hover:text-primary-300">
                        {item.title}
                      </span>
                      <span className="mt-1 block text-[11px] text-foreground-600">{item.tags[0]}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-4 rounded-md border border-accent-800/40 bg-accent-950/40 p-3">
              <p className="text-[11px] leading-relaxed text-accent-200">
                <i className="ri-information-line mr-1"></i>
                本站为虚构剧集的资料展示页面，不含在线播放与下载功能。
              </p>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}